import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { DmsModal, DmsButton, DmsTextField, intl, fetchData } from "dmsbasereact";
import {
    Box,
    useMediaQuery,
    useTheme,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Chip,
    Typography,
    CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";

const RoleFormModal = ({
    isVisible,
    onClose,
    onSave = null,
    role = null,
    isPermanent = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const isEditMode = Boolean(role);

    const [formName, setFormName] = useState("");
    const [formPermissions, setFormPermissions] = useState([]);
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [loadingPermissions, setLoadingPermissions] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadAvailablePermissions = useCallback(async () => {
        setLoadingPermissions(true);
        try {
            const response = await fetchData("/permission/all", "GET", true);
            const data = response?.data?.data || response?.data || [];
            setAvailablePermissions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to load available permissions:", error);
            setAvailablePermissions([]);
        } finally {
            setLoadingPermissions(false);
        }
    }, []);

    useEffect(() => {
        if (isVisible) {
            loadAvailablePermissions();
            if (role) {
                setFormName(role.role_name || "");
                setFormPermissions(role.permissions?.map((p) => p.permission_name) || []);
            } else {
                setFormName("");
                setFormPermissions([]);
            }
        }
    }, [isVisible, role, loadAvailablePermissions]);

    const handleSave = async () => {
        if (!formName.trim()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("MissingInformation").d("Missing Information"),
                text: intl.get("RoleNameRequired").d("Role name is required."),
            });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                role_name: formName.trim(),
                permissions: formPermissions,
            };

            let response;
            if (isEditMode) {
                response = await fetchData(`/role/${role.pk}`, "PUT", true, payload);
            } else {
                response = await fetchData("/role", "POST", true, payload);
            }

            if (response.status >= 200 && response.status < 300) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: isEditMode
                        ? intl.get("RoleUpdatedSuccessfully").d("Role updated successfully")
                        : intl.get("RoleCreatedSuccessfully").d("Role created successfully"),
                });
                if (onSave) onSave();
                onClose();
            } else {
                const errorMessage = response.data?.detail || intl.get("FailedToSaveRole").d("Failed to save role");
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: errorMessage,
                });
            }
        } catch (error) {
            console.error("Failed to save role:", error);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToSaveRole").d("Failed to save role"),
            });
        } finally {
            setSaving(false);
        }
    };

    const title = isEditMode
        ? intl.get("EditRole").d("Edit Role")
        : intl.get("CreateRole").d("Create Role");

    const isFormValid = formName.trim().length > 0;

    return (
        <DmsModal
            isVisible={isVisible}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
        >
            <DmsModal.Header title={title} onClose={onClose} />
            <DmsModal.Body>
                <Box sx={{ p: isMobile ? 1 : 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
                        {intl.get("BasicInfo").d("Basic Information")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DmsTextField
                                label={intl.get("RoleName").d("Role Name")}
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                fullWidth
                                required
                                disabled={isEditMode && isPermanent}
                                helperText={isEditMode && isPermanent ? intl.get("PermanentRoleNameCannotBeChanged").d("Permanent role name cannot be changed") : ""}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: "text.secondary" }}>
                        {intl.get("Permissions").d("Permissions")}
                    </Typography>
                    <FormControl fullWidth disabled={loadingPermissions}>
                        <InputLabel>{intl.get("SelectPermissions").d("Select Permissions")}</InputLabel>
                        <Select
                            multiple
                            value={formPermissions}
                            label={intl.get("SelectPermissions").d("Select Permissions")}
                            onChange={(e) => setFormPermissions(e.target.value)}
                            input={<OutlinedInput label={intl.get("SelectPermissions").d("Select Permissions")} />}
                            renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((permName) => (
                                        <Chip
                                            key={permName}
                                            label={permName}
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            )}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                    },
                                },
                            }}
                        >
                            {loadingPermissions ? (
                                <MenuItem disabled>
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    {intl.get("Loading").d("Loading...")}
                                </MenuItem>
                            ) : (
                                availablePermissions.map((permission) => (
                                    <MenuItem key={permission.pk} value={permission.permission_name}>
                                        <Checkbox checked={formPermissions.indexOf(permission.permission_name) > -1} />
                                        <ListItemText primary={permission.permission_name} />
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "flex-end",
                        gap: 1,
                        width: "100%",
                        p: isMobile ? 1 : 2,
                    }}
                >
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={onClose}
                        fullWidth={isMobile}
                        disabled={saving}
                    />
                    <DmsButton
                        variant="primary"
                        text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                        onClick={handleSave}
                        fullWidth={isMobile}
                        disabled={!isFormValid || saving}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

RoleFormModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    role: PropTypes.object,
    isPermanent: PropTypes.bool,
};


export { RoleFormModal };
